const SHORTLIST_PREFIX = "signature-shortlist";
export const SHORTLIST_UPDATED_EVENT = "signature-shortlist-updated";
const SHORTLIST_LIMIT = 12;

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export const normalizeShortlistSnapshot = (listing = {}) => ({
  id: String(listing.id || ""),
  title: listing.title || "Untitled Listing",
  district: listing.district || "",
  property_type: listing.property_type || "",
  category: listing.category || "",
  address: listing.address || "",
  image_url: listing.image_url || listing.gallery_urls?.[0] || "",
  owner_id: listing.owner_id || "",
  team_id: listing.team_id || "",
  price: Number(listing.price || 0),
  display_price: listing.display_price || "",
  sqft: Number(listing.sqft || 0),
  updated_at: listing.updated_at || new Date().toISOString(),
});

export const parseShortlistIds = (value = "") => Array.from(new Set(String(value || "").split(",").map((item) => item.trim()).filter(Boolean)));

export const getShortlistStorageKey = (scope = "global") => `${SHORTLIST_PREFIX}:${scope || "global"}`;

const readShortlistPayload = (scope = "global") => {
  if (typeof window === "undefined") return { items: [] };
  try {
    const stored = window.localStorage.getItem(getShortlistStorageKey(scope));
    if (!stored) return { items: [] };
    const parsed = JSON.parse(stored);
    return { items: Array.isArray(parsed?.items) ? parsed.items : [] };
  } catch {
    return { items: [] };
  }
};

export const readShortlistItems = (scope = "global") => readShortlistPayload(scope).items;

export const mergeShortlistItems = (currentItems = [], incomingItems = []) => {
  const mergedMap = new Map();
  [...currentItems, ...incomingItems]
    .map(normalizeShortlistSnapshot)
    .filter((item) => item.id)
    .forEach((item) => mergedMap.set(item.id, item));
  return Array.from(mergedMap.values()).slice(0, SHORTLIST_LIMIT);
};

export const writeShortlistItems = (scope = "global", items = []) => {
  if (typeof window === "undefined") return [];
  const normalizedItems = mergeShortlistItems([], items);
  const payload = { items: normalizedItems, updated_at: new Date().toISOString() };
  window.localStorage.setItem(getShortlistStorageKey(scope), JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(SHORTLIST_UPDATED_EVENT, { detail: { scope, items: normalizedItems } }));
  return normalizedItems;
};

export const toggleShortlistItem = (scope = "global", listing = {}) => {
  const snapshot = normalizeShortlistSnapshot(listing);
  const currentItems = readShortlistItems(scope);
  const exists = currentItems.some((item) => item.id === snapshot.id);
  const nextItems = exists
    ? currentItems.filter((item) => item.id !== snapshot.id)
    : mergeShortlistItems([snapshot], currentItems);
  writeShortlistItems(scope, nextItems);
  return { added: !exists, items: nextItems };
};

export const removeShortlistItem = (scope = "global", listingId = "") => {
  const nextItems = readShortlistItems(scope).filter((item) => item.id !== listingId);
  return writeShortlistItems(scope, nextItems);
};

export const clearShortlistItems = (scope = "global") => writeShortlistItems(scope, []);

export const buildShortlistSharePath = (buildBrandedPath, shortlistIds = []) => {
  const ids = parseShortlistIds(shortlistIds.join(","));
  return buildBrandedPath(`/listings${ids.length ? `?shortlist=${ids.join(",")}` : ""}`);
};

export const downloadShortlistCsv = (items = [], fileLabel = "signature-shortlist") => {
  if (typeof window === "undefined" || !items.length) return;
  const rows = [
    ["Title", "District", "Property Type", "Price", "Address", "Listing ID"],
    ...items.map((item) => [item.title, item.district, item.property_type, item.display_price || item.price, item.address, item.id]),
  ];
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = `${fileLabel}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
};